"use client";

import { useState, useCallback, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Scale, Loader2, Mail, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { OTPInput } from "@/components/auth/otp-input";
import { prepareOddsScreenAccess } from "@/lib/actions/odds-screen-gate";
import { categorizeOtpError, otpCooldownSeconds, otpErrorCopy } from "@/lib/auth/otp-errors";
import { phCapture } from "@/utils/analytics";

export type OddsGateReason = "anonymous" | "unauthenticated" | "client_locked";

type OddsEmailGateProps = {
  league: string;
  reason: OddsGateReason;
  onAccessGranted: () => void;
};

const RESEND_COOLDOWN = 60;

export function OddsEmailGate({ league, reason, onAccessGranted }: OddsEmailGateProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { sendOTPCode, verifyOTPAndSignIn, resendOTPCode } = useAuth();

  const redirectPath = (() => {
    const base = pathname ?? "/odds-screen";
    const search = searchParams?.toString();
    return search ? `${base}?${search}` : base;
  })();

  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [codeAlreadyRequested, setCodeAlreadyRequested] = useState(false);
  const [stepVisible, setStepVisible] = useState(true);
  const [existingAccount, setExistingAccount] = useState(false);

  const track = useCallback(
    (event: string, props?: Record<string, unknown>) =>
      phCapture(event, { surface: "odds_screen_gate", league, ...props }),
    [league],
  );

  useEffect(() => {
    track("odds_gate_viewed", { reason });
  }, [track, reason]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  const transitionTo = useCallback((nextStep: "email" | "otp") => {
    setStepVisible(false);
    setTimeout(() => {
      setStep(nextStep);
      setError(null);
      setOtp("");
      setStepVisible(true);
    }, 150);
  }, []);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || isSubmitting) return;

      setError(null);
      setIsSubmitting(true);

      try {
        track("odds_gate_email_submitted");
        const prep = await prepareOddsScreenAccess(email.trim());
        if (!prep.success) {
          track("odds_gate_prepare_failed", { reason: prep.code });
          setError(prep.error);
          setIsSubmitting(false);
          return;
        }
        setExistingAccount(prep.existingAccount);

        const { error: otpError } = await sendOTPCode(email.trim());

        if (otpError) {
          const cooldown = otpCooldownSeconds(otpError.message);
          if (cooldown !== null) {
            setCodeAlreadyRequested(true);
            transitionTo("otp");
            setResendTimer(cooldown);
            return;
          }
          setError(otpErrorCopy(categorizeOtpError(otpError.message), "send", otpError.message));
          return;
        }

        track("odds_gate_otp_sent", { existing_account: prep.existingAccount });
        setCodeAlreadyRequested(false);
        transitionTo("otp");
        setResendTimer(RESEND_COOLDOWN);
      } catch {
        setError("Something went wrong. Try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, isSubmitting, sendOTPCode, transitionTo, track]
  );

  const handleVerifyOTP = useCallback(async () => {
    if (otp.length !== 6 || isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const { error: verifyError } = await verifyOTPAndSignIn(email.trim(), otp);

      if (verifyError) {
        setError(otpErrorCopy(categorizeOtpError(verifyError.message), "verify", verifyError.message));
        setOtp("");
        return;
      }

      track("odds_gate_access_granted", { existing_account: existingAccount });
      onAccessGranted();
      router.refresh();
    } catch {
      setError("Verification failed. Try again.");
      setOtp("");
    } finally {
      setIsSubmitting(false);
    }
  }, [otp, isSubmitting, email, verifyOTPAndSignIn, onAccessGranted, router, track, existingAccount]);

  useEffect(() => {
    if (otp.length === 6 && !isSubmitting) handleVerifyOTP();
  }, [otp, isSubmitting, handleVerifyOTP]);

  const handleResend = useCallback(async () => {
    if (resendTimer > 0) return;
    setError(null);
    const { error: err } = await resendOTPCode(email.trim());
    if (err) {
      const cooldown = otpCooldownSeconds(err.message);
      if (cooldown !== null) {
        setCodeAlreadyRequested(true);
        setResendTimer(cooldown);
        return;
      }
      setError(otpErrorCopy(categorizeOtpError(err.message), "resend", err.message));
      return;
    }
    track("odds_gate_otp_resent");
    setCodeAlreadyRequested(false);
    setResendTimer(RESEND_COOLDOWN);
  }, [resendTimer, email, resendOTPCode, track]);

  const backToEmail = useCallback(() => {
    track("odds_gate_step_back", { from: "otp" });
    setCodeAlreadyRequested(false);
    transitionTo("email");
  }, [track, transitionTo]);

  return (
    <div className="flex items-center justify-center h-full min-h-[300px] relative">
      <p role="status" className="sr-only" data-ph-mask>
        {codeAlreadyRequested &&
          `A code was already requested for ${email.trim()} in the last minute. Enter it if you have it, or get a new one when the timer runs out.`}
      </p>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-[0.035] pointer-events-none select-none"
        aria-hidden="true"
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex gap-4 w-[90%] max-w-[700px]">
            <div className="h-4 rounded bg-zinc-400 flex-[2]" />
            <div className="h-4 rounded bg-zinc-400 flex-1" />
            {Array.from({ length: 5 }).map((_, j) => (
              <div key={j} className="h-4 w-16 rounded bg-zinc-400" />
            ))}
          </div>
        ))}
      </div>

      <div className="w-full max-w-[380px] mx-auto relative z-10">
        <div className="rounded-panel border border-white/[0.08] bg-surface-raised backdrop-blur-sm shadow-[0_0_40px_-12px_rgba(109,255,4,0.06)] overflow-hidden">
          <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

          <div
            className={cn(
              "px-6 py-8 sm:px-8 transition-opacity duration-150",
              stepVisible ? "opacity-100" : "opacity-0"
            )}
          >
            {step === "email" ? (
              <>
                <div className="flex flex-col items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-white/[0.04] border border-white/[0.08] shadow-[0_0_12px_-3px_rgba(109,255,4,0.15)]">
                    <Scale className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-center">
                    <h2 className="text-[15px] font-semibold text-zinc-100 leading-snug">
                      Compare odds across 20+ sportsbooks
                    </h2>
                    <p className="text-[13px] text-zinc-500 mt-1.5 leading-relaxed">
                      100% free. Just enter your email to unlock.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-3">
                  <div>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      required
                      disabled={isSubmitting}
                      variant="terminal"
                      className="h-11 text-sm"
                      aria-label="Email address"
                      autoComplete="email"
                    />
                    {error && (
                      <p className="text-[12px] text-destructive mt-1.5 px-0.5">
                        {error}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || !email.trim()}
                    className="w-full h-11 text-[13px] font-semibold"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending Code…
                      </span>
                    ) : (
                      "Get Free Access"
                    )}
                  </Button>
                </form>

                <div className="flex items-center justify-center gap-4 mt-4 text-[11px] text-zinc-500">
                  <span>No credit card</span>
                  <span className="text-zinc-700">|</span>
                  <span>Live odds</span>
                  <span className="text-zinc-700">|</span>
                  <span>No spam</span>
                </div>

                <div className="mt-3 pt-3 border-t border-white/[0.06] text-center">
                  <a
                    href={`/signin?redirect=${encodeURIComponent(redirectPath)}`}
                    className="text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Already have an account?{" "}
                    <span className="text-primary/80 hover:text-primary">
                      Sign In
                    </span>
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-white/[0.04] border border-white/[0.08]">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-center">
                    <h2 className="text-[15px] font-semibold text-zinc-100 leading-snug">
                      Enter your verification code
                    </h2>
                    <p className="text-[13px] text-zinc-500 mt-1.5">
                      {codeAlreadyRequested ? "A code was already requested for " : "Sent to "}
                      <span className="text-zinc-300 font-medium" data-ph-mask>
                        {email}
                      </span>
                      {codeAlreadyRequested &&
                        " in the last minute. Enter it if you have it, or get a new one when the timer runs out."}
                    </p>
                    <button
                      type="button"
                      onClick={backToEmail}
                      className="text-[12px] text-primary/60 hover:text-primary mt-0.5 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <OTPInput
                    value={otp}
                    onChange={(val) => {
                      setOtp(val);
                      setError(null);
                    }}
                    disabled={isSubmitting}
                    error={!!error}
                  />

                  {error && (
                    <p className="text-[12px] text-destructive text-center">
                      {error}
                    </p>
                  )}

                  {isSubmitting && (
                    <div className="flex items-center justify-center gap-2 text-[13px] text-zinc-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                      Verifying…
                    </div>
                  )}
                </div>

                <div className="mt-5 text-center">
                  {resendTimer > 0 ? (
                    <p className="text-[12px] text-zinc-600 tabular-nums">
                      Resend in {resendTimer}s
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      className="text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      Didn{"'"}t receive it?{" "}
                      <span className="text-primary/70 hover:text-primary">
                        Resend
                      </span>
                    </button>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-white/[0.06] text-center">
                  <button
                    type="button"
                    onClick={backToEmail}
                    className="inline-flex items-center gap-1 text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Different Email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
